import MUIAutocomplete, {
  AutocompleteInputChangeReason,
  AutocompletePaperSlotPropsOverrides,
  AutocompletePopperSlotPropsOverrides,
} from '@mui/material/Autocomplete';
import { isEmpty, omit, uniqBy } from 'lodash';
import { useCallback, useMemo, useRef } from 'react';
import { LoadingOption } from './Components/Options/LoadingOption';
import { Option } from './utils/models';
import { ChipProps } from 'common/components/Chips/Chip';
import Box from '@mui/material/Box';
import { TextOption } from './Components/Options/TextOption';
import Paper, { PaperProps } from '@mui/material/Paper';
import Popper, { PopperProps } from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import {
  generateBlankOption,
  generateErrorsOption,
  genereteInfinityLoadingOption,
  genereteLoadingOption,
  generteHintedOption,
} from './utils/optionsFactories';
import { OPTION_TYPE } from './utils/schemas';
import { HintedOption } from './Components/Options/HintedOption';
import { ErrorOption } from './Components/Options/ErrorOption';
import { t } from 'i18next';
import { CreateOption } from './Components/Options/CreateOption';
import { InView } from 'react-intersection-observer';
import { TextField } from '../TextField/TextField';
import { DEFAULT_BORDER_RADIUS } from 'src/theme/constants';

const StyledAutocomplete = styled(MUIAutocomplete)(({ theme }) => ({
  bgcolor: theme.palette.background.paper,
  '.MuiAutocomplete-inputRoot': {
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
    paddingLeft: theme.spacing(12),
    height: 'auto',
    minHeight: theme.spacing(40),
  },
  '& .MuiFormLabel-root': {
    textTransform: 'none',
  },
  '.MuiOutlinedInput-notchedOutline': {
    borderRadius: DEFAULT_BORDER_RADIUS,
  },
})) as typeof MUIAutocomplete;

const AutocompletePaper = styled(Paper)(({ theme }) => ({
  borderRadius: theme.spacing(16),
  padding: theme.spacing(8, 4, 8, 8),
  boxShadow: `0px 0px ${theme.spacing(12)} 0px #0000001F`,
  '.MuiAutocomplete-listbox': {
    padding: 0,
    borderRadius: theme.spacing(16),
  },
  '.autocomplete-list-box': {
    overflowX: 'clip',
  },
  '.autocomplete-list-box::-webkit-scrollbar': {
    width: theme.spacing(12),
    transition: '0.2s',
  },
  '.autocomplete-list-box::-webkit-scrollbar-thumb': {
    borderRadius: theme.spacing(8),
    backgroundColor: '#d9dbdd',
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
  '.autocomplete-list-box::-webkit-scrollbar-thumb:hover': {
    backgroundColor: '#B6BABA',
    border: 0,
  },
}));

const AutocompletePoper = styled(Popper)(({ theme }) => ({
  '& .MuiAutocomplete-groupLabel': {
    color: theme.palette.text.secondary,
    ...theme.typography.text1,
    padding: theme.spacing(8, 6, 8, 6),
  },
}));

const OptionListItem = styled('li')<{ selected?: boolean }>(({ selected, theme }) => ({
  height: theme.spacing(36),
  paddingLeft: theme.spacing(8),
  paddingRight: theme.spacing(6),
  backgroundColor: selected ? theme.palette.grey[200] : theme.palette.background.paper,

  display: 'flex',
  alignItems: 'center',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
}));

const AutocompleteTextField = styled(TextField)({
  '& [type="search"]::-webkit-search-decoration': {
    display: 'none',
  },
  '& [type="search"]::-webkit-search-cancel-button': {
    display: 'none',
  },
});

type AutocompleteProps = {
  group?: boolean;
  value: Option | null;
  options: Option[];
  hints?: Option[];
  errors?: string[];
  onHintRemove?: (hint: Option) => void;
  placeholder?: string;
  onChange?: (value: Option | null) => void;
  onSearch?: (value: string) => void;
  loading?: boolean;
  inputLoading?: boolean;
  skeletonRepeats?: number;
  showNameAndId?: boolean;
  disallowSelecting?: boolean;
  disallowSelectingReason?: string;
  'data-testid'?: string;
  variant?: ChipProps['variant'];
  creatable?: boolean;
  creatableMessage?: string;
  onCreate?: (value: string) => void;
  blankValueEnabled?: boolean;
  ref?: any;
  hasMore?: boolean;
  onMoreShouldBeLoad?: () => void;
  label?: string;
  disabled?: boolean;
};

const groupOption = (option: Option) => option.group || '';

const INFINITE_LOADING_OPTION = genereteInfinityLoadingOption();

export const SignleAutocomplete = ({
  group = false,
  label,
  value,
  options,
  hints,
  onHintRemove,
  placeholder,
  loading,
  onChange,
  onSearch,
  inputLoading,
  skeletonRepeats,
  showNameAndId,
  errors,
  disallowSelecting,
  disallowSelectingReason,
  creatable,
  creatableMessage,
  onCreate,
  blankValueEnabled,
  hasMore = false,
  onMoreShouldBeLoad,
  ...props
}: AutocompleteProps) => {
  const listAutocompleteRef = useRef<HTMLUListElement>(null);

  const handleOnChange = (_: React.SyntheticEvent, value: Option | null) => {
    if (onChange) {
      onChange(value);
    }
  };

  const handleOnSearch = (
    _: React.SyntheticEvent,
    search: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (onSearch) {
      onSearch(search);
    }

    if (reason === 'reset') {
      listAutocompleteRef.current?.scrollTo(0, 0);
    }
  };

  const optionsList = useMemo(() => {
    if (loading) {
      return [];
    }

    const errorsOptios = errors ? generateErrorsOption(errors) : [];
    const hintsOptions = hints ? generteHintedOption(hints) : [];
    const blankOption = blankValueEnabled ? generateBlankOption() : [];

    return [
      ...blankOption,
      ...errorsOptios,
      ...uniqBy([...hintsOptions, ...options], (option) => option.id),
    ];
  }, [loading, options, hints, errors]);

  const valueToRender = useMemo(() => {
    if (inputLoading) {
      return genereteLoadingOption();
    }

    return value;
  }, [value, inputLoading, skeletonRepeats]);

  return (
    <StyledAutocomplete
      {...props}
      loading={loading}
      renderInput={(params) => {
        return (
          <AutocompleteTextField
            {...params}
            label={label}
            slotProps={{
              inputLabel: { shrink: true },
            }}
            placeholder={placeholder}
          />
        );
      }}
      renderOption={(props, option, { selected }) => {
        const filteredProps = omit(props, ['className']);
        filteredProps.key = option.id;

        if ((disallowSelecting && !selected) || option.type === OPTION_TYPE.ERROR) {
          delete filteredProps['onClick'];
        }

        if (option.type === OPTION_TYPE.ERROR) {
          return (
            <OptionListItem {...filteredProps} aria-label={option.name}>
              <Box flex={1} pl={4}>
                <ErrorOption error={option.name} />
              </Box>
            </OptionListItem>
          );
        }

        if (option.type === OPTION_TYPE.INFINITE_LOADING) {
          return (
            <OptionListItem selected={selected} {...filteredProps} aria-label={'loading'}>
              <Box flex={1}>
                <InView
                  as="div"
                  onChange={(inView) => {
                    if (inView && onMoreShouldBeLoad) {
                      onMoreShouldBeLoad();
                    }
                  }}
                >
                  <LoadingOption />
                </InView>
              </Box>
            </OptionListItem>
          );
        }

        const commonProps = {
          disabledInfo: disallowSelectingReason,
          disabled: disallowSelecting,
          id: showNameAndId ? String(option.id) : undefined,
          name: option.name,
          selected,
        };

        const ariaLabel = showNameAndId ? `${option.name}, ${option.id}` : option.name;

        if (option.type === OPTION_TYPE.HINT) {
          return (
            <OptionListItem selected={selected} {...filteredProps} aria-label={ariaLabel}>
              <Box flex={1}>
                <HintedOption
                  checkbox={false}
                  {...commonProps}
                  onHintRemoveClick={() => {
                    if (onHintRemove) {
                      onHintRemove({ name: option.name, id: option.id });
                    }
                  }}
                />
              </Box>
            </OptionListItem>
          );
        }

        if (option.type === OPTION_TYPE.CREATE) {
          const message = creatableMessage || t('common.confirmToAdd');

          return (
            <OptionListItem
              {...filteredProps}
              onClick={() => {
                if (onCreate) {
                  onCreate(String(option.id));
                }
              }}
            >
              <Box flex={1}>
                <CreateOption {...commonProps} creatableMessage={message} />
              </Box>
            </OptionListItem>
          );
        }

        return (
          <OptionListItem selected={selected} {...filteredProps} aria-label={ariaLabel}>
            <Box flex={1}>
              <TextOption {...commonProps} checkbox={false} />
            </Box>
          </OptionListItem>
        );
      }}
      loadingText={<LoadingOption />}
      selectOnFocus
      value={valueToRender}
      options={optionsList}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => value.id === option.id}
      onChange={handleOnChange}
      onInputChange={handleOnSearch}
      filterOptions={(options, params) => {
        if (loading || !isEmpty(errors)) {
          return options;
        }

        if (hasMore) {
          return [...options, INFINITE_LOADING_OPTION];
        }

        if (!creatable) {
          return options;
        }

        const { inputValue } = params;
        if (!inputValue) {
          return options;
        }

        return options;
      }}
      slotProps={{
        listbox: {
          className: 'autocomplete-list-box',
          ref: listAutocompleteRef,
        },
      }}
      slots={{
        paper: useCallback(
          (params: PaperProps & AutocompletePaperSlotPropsOverrides) => (
            <AutocompletePaper {...params} />
          ),
          [],
        ),
        popper: useCallback(
          (params: PopperProps & AutocompletePopperSlotPropsOverrides) => (
            <AutocompletePoper {...params} />
          ),
          [],
        ),
      }}
      groupBy={group ? groupOption : undefined}
    />
  );
};
